import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Stack,
} from "@mui/material";
import {
  Close,
  CheckCircle,
  Cancel,
  UploadFile,
  ArrowBack,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PayoutRequest {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  wallet: number;
  wallet_balance: string;
  amount: string;
  method: string;
  payout_account: number;
  payout_account_snapshot: {
    method: string;
    masked_ecocash_number?: string;
    bank_name?: string;
    masked_account_number?: string;
    account_name?: string;
  };
  status: "PENDING" | "APPROVED" | "REJECTED" | "COMPLETED";
  requested_at: string;
  approved_by: number | null;
  processed_by: number | null;
  admin_note: string | null;
  completed_at: string | null;
  payout_account_details: {
    method: string;
    ecocash_number?: string;
    bank_name?: string;
    account_number?: string;
    account_name?: string;
  } | null;
  payment_proofs: {
    id: number;
    file: string;
    note: string | null;
    uploaded_at: string;
  }[];
}

interface AdminPayoutDetailDialogProps {
  open: boolean;
  payout: PayoutRequest | null;
  onClose: () => void;
  onUpdated: (updated: PayoutRequest) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const statusConfig = (status: PayoutRequest["status"]) => {
  if (status === "PENDING")
    return { label: "Pending", bgcolor: "#fff8e1", color: "#f59e0b" };
  if (status === "APPROVED")
    return { label: "Approved", bgcolor: "#e8f5e9", color: "#4caf50" };
  if (status === "REJECTED")
    return { label: "Rejected", bgcolor: "#ffebee", color: "#f44336" };
  return { label: "Completed", bgcolor: "#e3f2fd", color: "#1565c0" };
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getInitials = (name: string | undefined) =>
  (name ?? "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const MOBILE_METHODS = ["ECOCASH", "INNBUCKS", "OMARI", "ONEMONEY"];
const METHOD_LABELS: Record<string, string> = {
  ECOCASH: "Ecocash",
  INNBUCKS: "InnBucks",
  OMARI: "Omari",
  ONEMONEY: "OneMoney",
};

const accountSummary = (payout: PayoutRequest): string => {
  const details = payout.payout_account_details;
  if (details) {
    const name = details.account_name ? ` · ${details.account_name}` : "";
    if (MOBILE_METHODS.includes(details.method))
      return `${METHOD_LABELS[details.method] ?? details.method} — ${details.ecocash_number ?? ""}${name}`;
    return `${details.bank_name ?? "Bank"} — ${details.account_number ?? ""}${name}`;
  }
  const snap = payout.payout_account_snapshot;
  if (MOBILE_METHODS.includes(snap.method))
    return `${METHOD_LABELS[snap.method] ?? snap.method} — ${snap.masked_ecocash_number ?? ""}`;
  return `${snap.bank_name ?? "Bank"} — ${snap.masked_account_number ?? ""}`;
};

// ── Row helper ────────────────────────────────────────────────────────────────

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1,
    }}
  >
    <Typography variant="body2" sx={{ color: "#888", fontSize: "13px" }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        fontSize: "13px",
        textAlign: "right",
        maxWidth: "60%",
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ── Component ─────────────────────────────────────────────────────────────────

export default function AdminPayoutDetailDialog({
  open,
  payout,
  onClose,
  onUpdated,
}: AdminPayoutDetailDialogProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";
  const color10 = reduxTheme.primary_colors?.[10] ?? "#f5ede9";

  const [adminNote, setAdminNote] = useState("");
  const [loading, setLoading] = useState<
    "approve" | "reject" | "upload-complete" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [proofNote, setProofNote] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showRejectField, setShowRejectField] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!payout) return null;

  const { label, bgcolor, color } = statusConfig(payout.status);
  const initials = getInitials(payout.user_name);
  const isProcessing = loading !== null;

  const resetState = () => {
    setAdminNote("");
    setProofNote("");
    setSelectedFile(null);
    setError(null);
    setShowRejectField(false);
    setLoading(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // ── PENDING: Approve ──────────────────────────────────────────────────────

  const handleApprove = async () => {
    setError(null);
    setLoading("approve");
    try {
      const res = await apiService.post<PayoutRequest>(
        `/api/wallet/admin/payout-requests/${payout.id}/approve`,
        { admin_note: adminNote },
      );
      toast.success("Payout request approved.");
      onUpdated(res.data);
      setAdminNote("");
    } catch {
      toast.error("Failed to approve. Please try again.");
      setError("Failed to approve. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  // ── PENDING / APPROVED: Reject ────────────────────────────────────────────

  const handleReject = async () => {
    if (!adminNote.trim()) {
      setError("Please provide a reason for rejection.");
      return;
    }
    setError(null);
    setLoading("reject");
    try {
      const res = await apiService.post<PayoutRequest>(
        `/api/wallet/admin/payout-requests/${payout.id}/reject`,
        { admin_note: adminNote },
      );
      toast.success("Payout request rejected.");
      onUpdated(res.data);
      resetState();
    } catch {
      toast.error("Failed to reject. Please try again.");
      setError("Failed to reject. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  // ── APPROVED: Upload proof + complete in one action ───────────────────────

  const handleUploadAndComplete = async () => {
    if (!selectedFile) {
      setError("Please select a payment proof file first.");
      return;
    }
    setError(null);
    setLoading("upload-complete");
    try {
      // Step 1: upload proof
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (proofNote) formData.append("note", proofNote);

      await apiService.post(
        `/api/wallet/admin/payout-requests/${payout.id}/upload-proof`,
        formData,
      );

      // Step 2: mark complete
      const res = await apiService.post<PayoutRequest>(
        `/api/wallet/admin/payout-requests/${payout.id}/complete`,
        {},
      );
      toast.success("Payout marked as complete.");
      onUpdated(res.data);
      resetState();
    } catch {
      toast.error("Failed to process payment. Please try again.");
      setError("Failed to process payment. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: "rgba(255,255,255,0.15)",
              fontSize: 16,
              fontWeight: 700,
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize="15px"
              lineHeight={1.2}
            >
              {payout.user_name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.75, display: "block", mt: 0.2 }}
            >
              {payout.user_email}
            </Typography>
            <Chip
              label={label}
              size="small"
              sx={{
                mt: 0.8,
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor,
                color,
              }}
            />
          </Box>
        </Stack>

        {/* Amount */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            Requested Amount
          </Typography>
          <Typography variant="h4" fontWeight={700} fontSize="32px">
            ${parseFloat(payout.amount).toFixed(2)}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 2 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2, fontSize: "12px" }}
            >
              {error}
            </Alert>
          )}

          {/* Details */}
          <DetailRow
            label="Method"
            value={METHOD_LABELS[payout.method] ?? payout.method}
          />
          <Divider />
          <DetailRow label="Account" value={accountSummary(payout)} />
          <Divider />
          {payout.payout_account_details?.account_name && (
            <>
              <DetailRow
                label="Account Holder"
                value={payout.payout_account_details.account_name}
              />
              <Divider />
            </>
          )}
          <DetailRow
            label="Wallet Balance"
            value={`$${parseFloat(payout.wallet_balance).toFixed(2)}`}
          />
          <Divider />
          <DetailRow
            label="Requested"
            value={formatDate(payout.requested_at)}
          />
          {payout.completed_at && (
            <>
              <Divider />
              <DetailRow
                label="Completed"
                value={formatDate(payout.completed_at)}
              />
            </>
          )}
          {payout.admin_note && (
            <>
              <Divider />
              <DetailRow
                label="Admin Note"
                value={
                  <Typography
                    variant="body2"
                    sx={{
                      fontStyle: "italic",
                      color: "#555",
                      fontSize: "12px",
                    }}
                  >
                    {payout.admin_note}
                  </Typography>
                }
              />
            </>
          )}
        </Box>

        {/* Payment Proofs */}
        {payout.payment_proofs.length > 0 && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontWeight: 600,
              }}
            >
              Payment Proofs
            </Typography>
            {payout.payment_proofs.map((proof) => (
              <Box
                key={proof.id}
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: "#f8f9fa",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "12px", fontWeight: 500 }}
                  >
                    {proof.note || "Proof of payment"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#888", fontSize: "11px" }}
                  >
                    {formatDate(proof.uploaded_at)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  href={proof.file}
                  target="_blank"
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    color: color400,
                  }}
                >
                  View
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ px: 3, py: 2.5 }}>
          {/* ── PENDING: Approve or Reject ──────────────────────────────── */}
          {payout.status === "PENDING" && (
            <>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Admin note (required for rejection, optional for approval)"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "13px",
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    loading === "reject" ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Cancel />
                    )
                  }
                  onClick={handleReject}
                  disabled={isProcessing}
                  sx={{
                    borderColor: "#ff4d6d",
                    color: "#ff4d6d",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#fff0f3", borderColor: "#ff4d6d" },
                  }}
                >
                  Reject
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={
                    loading === "approve" ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <CheckCircle />
                    )
                  }
                  onClick={handleApprove}
                  disabled={isProcessing}
                  sx={{
                    bgcolor: "#00b894",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#00a381", boxShadow: "none" },
                  }}
                >
                  Approve
                </Button>
              </Box>
            </>
          )}

          {/* ── APPROVED: Upload proof + complete in one step ───────────── */}
          {payout.status === "APPROVED" && (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: "#888",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "block",
                  mb: 1.5,
                }}
              >
                Process Payment
              </Typography>

              {/* File drop zone */}
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  mb: 1.5,
                  p: 2.5,
                  border: `2px dashed ${selectedFile ? "#4caf50" : "#e0e0e0"}`,
                  borderRadius: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: selectedFile ? "#f0fdf4" : "#fafafa",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: color400, bgcolor: "#faf7f5" },
                }}
              >
                {selectedFile && selectedFile.type.startsWith("image/") ? (
                  <Box
                    component="img"
                    src={URL.createObjectURL(selectedFile)}
                    alt="proof preview"
                    sx={{
                      width: "100%",
                      maxHeight: 160,
                      objectFit: "contain",
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  />
                ) : (
                  <UploadFile
                    sx={{
                      color: selectedFile ? "#4caf50" : "#bbb",
                      fontSize: 30,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    color: selectedFile ? "#4caf50" : "#888",
                    mt: 0.5,
                  }}
                >
                  {selectedFile
                    ? selectedFile.type.startsWith("image/")
                      ? "Tap to change image"
                      : selectedFile.name
                    : "Click to select proof of payment (PDF, PNG, JPG)"}
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => {
                    setSelectedFile(e.target.files?.[0] ?? null);
                    setError(null);
                  }}
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Payment reference or note (optional)"
                value={proofNote}
                onChange={(e) => setProofNote(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "13px",
                  },
                }}
              />

              {/* Single primary action — upload + complete together */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleUploadAndComplete}
                disabled={isProcessing || !selectedFile}
                startIcon={
                  loading === "upload-complete" ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <CheckCircle />
                  )
                }
                sx={{
                  background: selectedFile
                    ? `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`
                    : undefined,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.3,
                  boxShadow: "none",
                  mb: 1.5,
                }}
              >
                {loading === "upload-complete"
                  ? "Processing…"
                  : "Upload Proof & Complete"}
              </Button>

              {/* Reject as low-emphasis option */}
              {!showRejectField ? (
                <Box sx={{ textAlign: "center" }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowRejectField(true);
                      setError(null);
                    }}
                    disabled={isProcessing}
                    sx={{
                      textTransform: "none",
                      fontSize: "12px",
                      color: "#f44336",
                      "&:hover": {
                        bgcolor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Reject this request instead
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Reason for rejection (required)"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    sx={{
                      mb: 1.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        fontSize: "13px",
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setShowRejectField(false);
                        setAdminNote("");
                        setError(null);
                      }}
                      disabled={isProcessing}
                      sx={{
                        textTransform: "none",
                        color: "#888",
                        flex: 1,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleReject}
                      disabled={isProcessing}
                      startIcon={
                        loading === "reject" ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Cancel fontSize="small" />
                        )
                      }
                      sx={{
                        flex: 1,
                        borderColor: "#f44336",
                        color: "#f44336",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: "#fff0f3",
                          borderColor: "#f44336",
                        },
                      }}
                    >
                      Confirm Reject
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* ── REJECTED / COMPLETED: read only ────────────────────────── */}
          {(payout.status === "REJECTED" || payout.status === "COMPLETED") && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ArrowBack fontSize="small" />}
              onClick={handleClose}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                borderColor: "#e0e0e0",
                color: "#666",
              }}
            >
              Close
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
